/* Options → Rules sub-tab. Pure reference checklist (2026-02). The
   previous 16-rule lock on the Trade Planner was removed — the Trade
   Planner is now always accessible. The checklist is still useful as a
   personal "tick as you read" reference; progress persists to localStorage
   under `dt_options_rules_confirmed`. */

import { useState } from "react";
import { RULE_CATEGORIES, RULES_LS_KEY, TOTAL_RULES } from "./_optionsShared";

// ── Session-3 redesign tokens (kept local — spec says no _shared edits)
const T = {
  card:        { background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:8, padding:"16px 20px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" },
  sectionLab:  { fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em" },
  secondaryBtn:{ background:"#fff", color:"#64748b", border:"1px solid #e2e8f0", borderRadius:6, padding:"6px 14px", fontSize:12, cursor:"pointer" },
};
const CAT_COLORS = { core:"#4f46e5", entry:"#16a34a", exit:"#d97706", "no-trade":"#dc2626" };

export default function OptionsRules() {
  // Local state — no longer lifted to the parent since nothing else
  // depends on rule-confirmation progress.
  const [confirmed, setConfirmed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RULES_LS_KEY) || "{}"); } catch { return {}; }
  });
  const checked = Object.values(confirmed).filter(Boolean).length;

  function toggleRule(id) {
    setConfirmed(c => {
      const next = { ...c, [id]: !c[id] };
      try { localStorage.setItem(RULES_LS_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }
  function resetAll() {
    if (!window.confirm("Reset all rule confirmations? Your tick progress will be cleared.")) return;
    setConfirmed({});
    try { localStorage.removeItem(RULES_LS_KEY); } catch { /* noop */ }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Slim header — pure reference, no gating language. */}
      <div data-testid="options-rules-header" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, padding: "8px 14px",
        background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8,
      }}>
        <span style={{ fontSize: 12, color: "#475569" }}>
          16 trading rules — tick as you read for your own reference.
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span data-testid="options-rules-progress" style={{ fontFamily:"monospace", fontWeight:700, fontSize: 12, color: "#64748b" }}>
            {checked}/{TOTAL_RULES}
          </span>
          <button onClick={resetAll} data-testid="options-rules-reset-btn" style={T.secondaryBtn}>
            Reset
          </button>
        </div>
      </div>

      {RULE_CATEGORIES.map(cat => {
        const accent = CAT_COLORS[cat.id] || "#4f46e5";
        return (
          <div key={cat.id} style={T.card}>
            <div style={{ ...T.sectionLab, color: accent, borderLeft: `3px solid ${accent}`, paddingLeft: 10, marginBottom: 12 }}>
              {cat.title}
            </div>
            {cat.rules.map((rule, idx) => {
              const id = `${cat.id}-${idx}`;
              const on = !!confirmed[id];
              return (
                <label key={id} data-testid={`options-rule-${id}`}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 0", cursor: "pointer",
                    borderBottom: idx < cat.rules.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}>
                  <span style={{ fontSize: 10, fontFamily:"monospace", color: "#94a3b8", width: 24, flexShrink: 0, marginTop: 3 }}>{String(idx + 1).padStart(2, "0")}</span>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleRule(id)}
                    style={{ accentColor: "#4f46e5", width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
                  />
                  <span style={{
                    fontSize: 13, lineHeight: 1.6,
                    color: on ? "#94a3b8" : "#0f172a",
                  }}>{rule}</span>
                </label>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
